import { useEffect, useMemo, useState } from "react";
import { ArrowRight, BookOpen, Search } from "lucide-react";
import { PageHeader, StatusPill } from "../components/Primitives";
import { Modal } from "../components/Modal";
import { api, errorText, formatDate } from "../lib/api";

export default function Books() {
  const [books, setBooks] = useState([]);
  const [issues, setIssues] = useState([]);
  const [students, setStudents] = useState([]);
  const [tab, setTab] = useState("catalogue");
  const [query, setQuery] = useState("");
  const [error, setError] = useState("");
  const [show, setShow] = useState(false);
  const [issueShow, setIssueShow] = useState(false);
  const [form, setForm] = useState({ name: "", author: "", category: "", quantity: 1, shelf: "" });
  const [issueForm, setIssueForm] = useState({ student_id: "", book_id: "", expected_return_date: "" });

  const load = async () => {
    try {
      const [booksRes, issuesRes, studentsRes] = await Promise.all([
        api.get("/books"),
        api.get("/book-issues"),
        api.get("/students"),
      ]);
      setBooks(booksRes.data.data);
      setIssues(issuesRes.data.data);
      setStudents(studentsRes.data.data);
    } catch (err) {
      setError(errorText(err));
    }
  };

  useEffect(() => {
    load();
  }, []);

  const filteredBooks = useMemo(() => {
    if (!query) return books;
    const needle = query.toLowerCase();
    return books.filter((book) =>
      [book.name, book.author, book.isbn, book.category].some((value) => value?.toLowerCase().includes(needle))
    );
  }, [books, query]);

  const addBook = async (event) => {
    event.preventDefault();
    try {
      await api.post("/books", { ...form, quantity: Number(form.quantity) });
      setShow(false);
      setForm({ name: "", author: "", category: "", quantity: 1, shelf: "" });
      load();
    } catch (err) {
      setError(errorText(err));
    }
  };

  const issueBook = async (event) => {
    event.preventDefault();
    try {
      const payload = { ...issueForm };
      if (!payload.expected_return_date) delete payload.expected_return_date;
      await api.post("/book-issues", payload);
      setIssueShow(false);
      setIssueForm({ student_id: "", book_id: "", expected_return_date: "" });
      load();
    } catch (err) {
      setError(errorText(err));
    }
  };

  const returnBook = async (issue, status = "RETURNED") => {
    try {
      await api.post(`/book-issues/${issue.id}/return`, { status });
      load();
    } catch (err) {
      setError(errorText(err));
    }
  };

  const promptReturnStatus = (issue) => {
    const choice = (window.prompt("Change status to: RETURNED, LOST or DAMAGED", "RETURNED") || "").toUpperCase().trim();
    if (!["RETURNED", "LOST", "DAMAGED"].includes(choice)) return;
    returnBook(issue, choice);
  };

  const reopenIssue = async (issue) => {
    if (!window.confirm(`Reopen "${issue.book_name}" and mark it as ISSUED again?`)) return;
    try {
      await api.post(`/book-issues/${issue.id}/reopen`);
      load();
    } catch (err) {
      setError(errorText(err));
    }
  };

  return (
    <div className="page-enter">
      <PageHeader
        eyebrow="LIBRARY CATALOGUE"
        title="Books"
        description="Catalogue, issue and return every title with the student history intact."
        action={
          <div style={{ display: "flex", gap: 10 }}>
            <button className="button button-outline" onClick={() => setIssueShow(true)} data-testid="issue-book-button">
              Issue book
            </button>
            <button className="button button-primary" onClick={() => setShow(true)} data-testid="add-book-button">
              <BookOpen size={16} /> Add book
            </button>
          </div>
        }
      />
      {error ? <div className="error-message">{error}</div> : null}

      <div className="tabs" data-testid="books-tabs">
        <button className={`tab ${tab === "catalogue" ? "active" : ""}`} onClick={() => setTab("catalogue")} data-testid="catalogue-tab">
          Catalogue ({books.length})
        </button>
        <button className={`tab ${tab === "issues" ? "active" : ""}`} onClick={() => setTab("issues")} data-testid="issues-tab">
          Issues ({issues.length})
        </button>
      </div>

      {tab === "catalogue" ? (
        <section className="content-card table-card">
          <div className="table-toolbar">
            <div>
              <span className="card-kicker">CATALOGUE</span>
              <h2>{filteredBooks.length} titles</h2>
            </div>
            <div className="table-search">
              <Search size={17} />
              <input placeholder="Search books" value={query} onChange={(event) => setQuery(event.target.value)} data-testid="books-search-input" />
            </div>
          </div>
          <div className="table-wrap">
            <table data-testid="books-table">
              <thead>
                <tr><th>Book</th><th>Author</th><th>Category</th><th>Available / total</th><th>Shelf</th></tr>
              </thead>
              <tbody>
                {filteredBooks.map((book) => (
                  <tr key={book.id} data-testid={`book-row-${book.id}`}>
                    <td><strong>{book.name}</strong></td>
                    <td>{book.author || "—"}</td>
                    <td>{book.category || "—"}</td>
                    <td>{book.available} / {book.quantity}</td>
                    <td>{book.shelf || "—"}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </section>
      ) : (
        <section className="content-card table-card">
          <div className="table-toolbar">
            <div>
              <span className="card-kicker">BOOK ISSUES</span>
              <h2>{issues.length} records</h2>
            </div>
          </div>
          <div className="table-wrap">
            <table data-testid="book-issues-table">
              <thead>
                <tr><th>Book</th><th>Student</th><th>Issued</th><th>Expected return</th><th>Status</th><th>Action</th></tr>
              </thead>
              <tbody>
                {issues.map((issue) => (
                  <tr key={issue.id} data-testid={`issue-row-${issue.id}`}>
                    <td>{issue.book_name}</td>
                    <td>{issue.student_name}</td>
                    <td>{formatDate(issue.issue_date)}</td>
                    <td>{formatDate(issue.expected_return_date)}</td>
                    <td><StatusPill status={issue.status} /></td>
                    <td>
                      {issue.status === "ISSUED" ? (
                        <div className="action-row">
                          <button className="button button-outline button-small" onClick={() => returnBook(issue, "RETURNED")} data-testid={`return-book-${issue.id}`}>
                            Mark returned
                          </button>
                          <button className="button button-outline button-small" onClick={() => promptReturnStatus(issue)} data-testid={`change-issue-status-${issue.id}`} title="Mark as LOST or DAMAGED">
                            Change status
                          </button>
                        </div>
                      ) : (
                        <div className="action-row">
                          <span style={{ color: "#64748b", fontSize: 12 }}>{formatDate(issue.return_date)}</span>
                          <button className="button button-outline button-small" onClick={() => reopenIssue(issue)} data-testid={`reopen-issue-${issue.id}`} title="Undo — mark as ISSUED again">
                            Reopen
                          </button>
                        </div>
                      )}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </section>
      )}

      {show ? (
        <Modal title="Add book" eyebrow="CATALOGUE" close={() => setShow(false)} testId="add-book-modal">
          <form className="modal-form" onSubmit={addBook}>
            <label>Name<input type="text" required value={form.name} onChange={(event) => setForm({ ...form, name: event.target.value })} data-testid="book-name-input" /></label>
            <div className="form-split">
              <label>Author<input type="text" value={form.author} onChange={(event) => setForm({ ...form, author: event.target.value })} data-testid="book-author-input" /></label>
              <label>Category<input type="text" value={form.category} onChange={(event) => setForm({ ...form, category: event.target.value })} data-testid="book-category-input" /></label>
            </div>
            <div className="form-split">
              <label>Quantity<input type="number" min="0" value={form.quantity} onChange={(event) => setForm({ ...form, quantity: event.target.value })} data-testid="book-quantity-input" /></label>
              <label>Shelf<input type="text" value={form.shelf} onChange={(event) => setForm({ ...form, shelf: event.target.value })} data-testid="book-shelf-input" /></label>
            </div>
            <div className="modal-actions">
              <button type="button" className="button button-outline" onClick={() => setShow(false)}>Cancel</button>
              <button className="button button-primary" type="submit" data-testid="save-book-button">
                Save book <ArrowRight size={16} />
              </button>
            </div>
          </form>
        </Modal>
      ) : null}

      {issueShow ? (
        <Modal title="Issue book" eyebrow="BOOK ISSUE" close={() => setIssueShow(false)} testId="issue-book-modal">
          <form className="modal-form" onSubmit={issueBook}>
            <label>Student
              <select required value={issueForm.student_id} onChange={(event) => setIssueForm({ ...issueForm, student_id: event.target.value })} data-testid="issue-student-select">
                <option value="">Select student</option>
                {students.map((student) => (
                  <option key={student.id} value={student.id}>{student.name} · {student.student_id}</option>
                ))}
              </select>
            </label>
            <label>Book
              <select required value={issueForm.book_id} onChange={(event) => setIssueForm({ ...issueForm, book_id: event.target.value })} data-testid="issue-book-select">
                <option value="">Select book</option>
                {books.filter((book) => book.available > 0).map((book) => (
                  <option key={book.id} value={book.id}>{book.name} (avail: {book.available})</option>
                ))}
              </select>
            </label>
            <label>Expected return date<input type="date" value={issueForm.expected_return_date} onChange={(event) => setIssueForm({ ...issueForm, expected_return_date: event.target.value })} /></label>
            <div className="modal-actions">
              <button type="button" className="button button-outline" onClick={() => setIssueShow(false)}>Cancel</button>
              <button className="button button-primary" type="submit" data-testid="save-issue-button">
                Issue book <ArrowRight size={16} />
              </button>
            </div>
          </form>
        </Modal>
      ) : null}
    </div>
  );
}
